import React, { useEffect, useState } from 'react';
import { listQuizzes, Quiz, getQuiz, startQuizAttempt, submitQuizResponses, getAttemptResults, createQuiz, updateQuiz, getQuizResults, gradeQuizResponse } from '../services/quizService';
import { getCourses } from '../services/courseService';
import { useAuth } from '../context/AuthContext';
import { useFeedback } from '../context/FeedbackContext';
import { Course } from '../types';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, Button, Box, Alert, CircularProgress, GridLegacy as Grid, MenuItem } from '@mui/material';
import dayjs from 'dayjs';

// Helper for new questions
const emptyQuestion = () => ({
  question_text: '',
  question_type: 'mcq',
  points: 1,
  options: [{ option_text: '', is_correct: false }, { option_text: '', is_correct: false }],
});

// Add this near the top, after imports:
const classLevels = [
  'Pre-Primary 1', 'Pre-Primary 2',
  'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
  'Grade 6', 'Grade 7', 'Grade 8',
  'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
];

// --- Quiz Taking Dialog ---
type QuizTakingDialogProps = {
  quizId: number;
  onClose: () => void;
};

const QuizTakingDialog: React.FC<QuizTakingDialogProps> = ({ quizId, onClose }) => {
  const { user } = useAuth();
  const { showError, showSuccess } = useFeedback();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<number, any>>({}); // question_id -> answer
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState<any | null>(null);

  useEffect(() => {
    async function fetchQuiz() {
      setLoading(true);
      try {
        const data = await getQuiz(quizId);
        setQuiz(data);
        if (data.duration_minutes) {
          setTimer(data.duration_minutes * 60); // seconds
          setTimeLeft(data.duration_minutes * 60);
        }
      } catch (e: any) {
        showError(e?.response?.data?.detail || 'Failed to load quiz');
      } finally {
        setLoading(false);
      }
    }
    fetchQuiz();
  }, [quizId, showError]);

  // Start attempt on mount (if not already started)
  useEffect(() => {
    if (!quiz || attemptId) return;
    async function startAttempt() {
      try {
        if (!quiz || !user?.id) return;
        const attempt = await startQuizAttempt(quiz.id, { quiz_id: quiz.id, student_id: user.id! });
        setAttemptId((attempt as any).id);
      } catch (e: any) {
        showError(e?.response?.data?.detail || 'Failed to start attempt');
        setError(e?.response?.data?.detail || 'Failed to start attempt');
      }
    }
    startAttempt();
  }, [quiz, attemptId, user?.id, showError]);

  // Prepare responses for submission
  const getResponses = () => {
    if (!quiz || !quiz.questions) return [];
    return quiz.questions.map((q: any) => {
      const ans = answers[q.id] || {};
      if (q.question_type === 'mcq' || q.question_type === 'true_false') {
        return { question_id: q.id, selected_option_id: ans.selected_option_id };
      } else if (q.question_type === 'short_answer') {
        return { question_id: q.id, answer_text: ans.answer_text };
      }
      return { question_id: q.id };
    });
  };

  // Check if all questions are answered
  const allAnswered = quiz && quiz.questions && quiz.questions.every((q: any) => {
    const ans = answers[q.id];
    if (q.question_type === 'mcq' || q.question_type === 'true_false') {
      return ans && ans.selected_option_id !== undefined && ans.selected_option_id !== null && ans.selected_option_id !== '';
    } else if (q.question_type === 'short_answer') {
      return ans && ans.answer_text && ans.answer_text.trim() !== '';
    }
    return false;
  });

  const handleSubmit = async () => {
    if (!quiz || !attemptId) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitQuizResponses(quiz.id, attemptId, getResponses());
      setSuccess(true);
      showSuccess('Quiz submitted!');
      // Fetch results for feedback
      const res = await getAttemptResults(attemptId);
      setResult(res);
      setTimeout(() => {
        onClose();
      }, 3500);
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Submission failed');
      setError(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Timer countdown
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  useEffect(() => {
    if (timeLeft === 0) {
      // Auto-submit on timeout
      if (allAnswered && !submitting && !success) {
        handleSubmit();
      } else {
        alert('Time is up! Some questions were not answered.');
        onClose();
      }
    }
  }, [timeLeft, allAnswered, submitting, success, onClose]);

  if (!quiz) return null;

  const handleOptionChange = (questionId: number, optionId: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { selected_option_id: optionId } }));
  };
  const handleTrueFalseChange = (questionId: number, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { selected_option_id: value } }));
  };
  const handleShortAnswerChange = (questionId: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { answer_text: value } }));
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: '70%', lg: '60%' },
          minHeight: '60vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>{quiz.title}</DialogTitle>
      {quiz.duration_minutes && timeLeft !== null && (
        <Box sx={{ fontWeight: 'bold', color: timeLeft < 60 ? 'red' : undefined }}>
          Time left: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
        </Box>
      )}
      <DialogContent>
        <Box sx={{ p: 2 }}>
          {quiz.questions && quiz.questions.length > 0 ? (
            <ol>
              {quiz.questions.map((q: any, idx: number) => (
                <Box component="li" key={q.id} sx={{ marginBottom: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{q.question_text}</Box>
                    <Box sx={{ color: '#888' }}>({q.points} pt{q.points !== 1 ? 's' : ''})</Box>
                  </Box>
                  {q.question_type === 'mcq' && q.options && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                      {q.options.map((opt: any) => (
                        <Box key={opt.id} sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 30%' }, display: 'flex', alignItems: 'center' }}>
                          <input
                            type="radio"
                            name={`q_${q.id}`}
                            value={opt.id}
                            checked={answers[q.id]?.selected_option_id === opt.id}
                            onChange={() => handleOptionChange(q.id, opt.id)}
                            style={{ marginRight: 8 }}
                          />{' '}
                          {opt.option_text}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {q.question_type === 'true_false' && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          name={`q_${q.id}`}
                          value="true"
                          checked={answers[q.id]?.selected_option_id === true}
                          onChange={() => handleTrueFalseChange(q.id, true)}
                          style={{ marginRight: 8 }}
                        /> True
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          name={`q_${q.id}`}
                          value="false"
                          checked={answers[q.id]?.selected_option_id === false}
                          onChange={() => handleTrueFalseChange(q.id, false)}
                          style={{ marginLeft: 2 }}
                        /> False
                      </Box>
                    </Box>
                  )}
                  {q.question_type === 'short_answer' && (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        value={answers[q.id]?.answer_text || ''}
                        onChange={e => handleShortAnswerChange(q.id, e.target.value)}
                        placeholder="Your answer"
                        sx={{ marginLeft: 2 }}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </ol>
          ) : (
            <Box sx={{ textAlign: 'center' }}><em>No questions found for this quiz.</em></Box>
          )}
        </Box>
      </DialogContent>
      {error && (
        <Alert severity="error" sx={{ marginBottom: 1, marginLeft: 2, marginRight: 2 }}>
          {error}
        </Alert>
      )}
      {success && result && quiz && Array.isArray(quiz.questions) && (
        <Alert severity="success" sx={{ marginBottom: 1, marginLeft: 2, marginRight: 2 }}>
          <Box sx={{ fontWeight: 'bold' }}>Quiz submitted!</Box><br />
          Score: {quiz && Array.isArray(quiz.questions) ? result.score + ' / ' + quiz.questions.reduce((sum: number, q: any) => sum + q.points, 0) : ''}
          <ul style={{ marginTop: 8 }}>
            {result.responses.map((resp: any, idx: number) => {
              const q = quiz && Array.isArray(quiz.questions) ? quiz.questions.find((qq: any) => qq.id === resp.question_id) : undefined;
              return (
                <li key={resp.question_id} style={{ color: resp.is_correct === true ? 'green' : resp.is_correct === false ? 'red' : '#888' }}>
                  Q{idx + 1}: {q?.question_text || ''} — {resp.is_correct === true ? 'Correct' : resp.is_correct === false ? 'Incorrect' : 'Pending Review'}
                </li>
              );
            })}
          </ul>
        </Alert>
      )}
      <DialogActions>
        <Button onClick={onClose} disabled={submitting || success}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!allAnswered || submitting || success}>
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Modernized Quiz Create Dialog ---
interface QuizCreateDialogProps {
  onClose: () => void;
  onCreated: () => void;
  initialQuiz?: Quiz | null;
}

const QuizCreateDialog: React.FC<QuizCreateDialogProps> = ({ onClose, onCreated, initialQuiz }) => {
  const { user } = useAuth();
  const { showError, showSuccess } = useFeedback();
  const [title, setTitle] = useState(initialQuiz?.title || '');
  const [description, setDescription] = useState(initialQuiz?.description || '');
  const [duration, setDuration] = useState<number | ''>(initialQuiz?.duration_minutes ?? '');
  const [courseId, setCourseId] = useState<number | ''>(initialQuiz?.course_id ?? '');
  const [courses, setCourses] = useState<Course[]>([]);
  const [questions, setQuestions] = useState<any[]>(initialQuiz?.questions ? initialQuiz.questions.map(q => ({ ...q, options: q.options ? [...q.options] : [] })) : [emptyQuestion()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [classLevel, setClassLevel] = useState<string>(initialQuiz?.class_level || '');
  const [mode, setMode] = useState<string>(initialQuiz?.mode || 'passive');
  const [startTime, setStartTime] = useState<string>(initialQuiz?.start_time ? dayjs(initialQuiz.start_time).format('YYYY-MM-DDTHH:mm') : '');
  const [endTime, setEndTime] = useState<string>(initialQuiz?.end_time ? dayjs(initialQuiz.end_time).format('YYYY-MM-DDTHH:mm') : '');

  useEffect(() => {
    if (user?.school_id) {
      getCourses(user.school_id, classLevel).then(setCourses);
      setCourseId(prev => {
        if (!prev) return '';
        const found = courses.find(c => c.id === prev && c.class_level === classLevel);
        return found ? prev : '';
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.school_id, classLevel]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'live' && (!startTime || !endTime)) {
        setError('Start and end time are required for live quizzes.');
        setSubmitting(false);
        return;
      }
      const quizPayload = {
        title,
        description,
        duration_minutes: duration === '' ? null : Number(duration),
        school_id: user?.school_id,
        course_id: courseId === '' ? null : courseId,
        class_level: classLevel,
        mode,
        start_time: mode === 'live' ? (startTime ? new Date(startTime).toISOString() : null) : null,
        end_time: mode === 'live' ? (endTime ? new Date(endTime).toISOString() : null) : null,
        is_active: true,
        questions: questions.map(q => ({
          question_text: q.question_text,
          question_type: q.question_type,
          points: q.points,
          options: q.question_type !== 'short_answer' ? q.options : undefined,
        })),
      };
      if (initialQuiz) {
        await updateQuiz(initialQuiz.id, quizPayload as any);
        showSuccess('Quiz updated successfully');
      } else {
        await createQuiz(quizPayload as any);
        showSuccess('Quiz created successfully');
      }
      onCreated();
      onClose();
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Failed to save quiz');
      setError(e?.response?.data?.detail || 'Failed to save quiz');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: '70%', lg: '60%' },
          minHeight: '70vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>{initialQuiz ? 'Edit Quiz' : 'Create Quiz'}</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
              <TextField
                fullWidth
                label="Title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                error={!title}
                helperText={!title ? 'Title is required' : ''}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                multiline
                rows={2}
              />
            </Box>
            {/* Move Class Level above Course */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
              <TextField
                fullWidth
                label="Class Level"
                select
                value={classLevel}
                onChange={e => setClassLevel(e.target.value)}
                error={!classLevel}
                helperText={!classLevel ? 'Class level is required' : ''}
              >
                <MenuItem value='' disabled>Select class level</MenuItem>
                {classLevels.map(level => <MenuItem key={level} value={level}>{level}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
              <TextField
                fullWidth
                label="Course"
                select
                value={courseId}
                onChange={e => setCourseId(Number(e.target.value))}
                error={!courseId}
                helperText={!courseId ? 'Course is required' : ''}
              >
                <MenuItem value='' disabled>Select course</MenuItem>
                {courses.map((c: Course) => <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                inputProps={{ min: 1 }}
                value={duration}
                onChange={e => setDuration(e.target.value === '' ? '' : Number(e.target.value))}
                error={!duration}
                helperText={!duration ? 'Duration is required' : ''}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
              <TextField
                fullWidth
                label="Quiz Mode"
                select
                value={mode}
                onChange={e => setMode(e.target.value)}
              >
                <MenuItem value="passive">Passive (open window, self-paced)</MenuItem>
                <MenuItem value="live">Live (scheduled, real-time)</MenuItem>
              </TextField>
            </Box>
            {mode === 'live' && (
              <>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
                  <TextField
                    fullWidth
                    label="Start Time"
                    type="datetime-local"
                    value={startTime}
                    onChange={e => setStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
                  <TextField
                    fullWidth
                    label="End Time"
                    type="datetime-local"
                    value={endTime}
                    onChange={e => setEndTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </>
            )}
          </Box>
          {/* Questions */}
          <Box sx={{ marginTop: 3, marginBottom: 1 }}>
            <Box sx={{ fontWeight: 'bold', marginBottom: 1, fontSize: '1.2rem' }}>Questions</Box>
            {questions.map((q: any, index: number) => (
              <Box key={q.id || index} sx={{ border: '1px solid #ccc', borderRadius: 1, padding: 2, marginBottom: 2, background: '#fafbfc' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 60%' } }}>
                    <TextField
                      fullWidth
                      label={`Question ${index + 1}`}
                      value={q.question_text}
                      onChange={e => setQuestions(prev => prev.map((qq: any, i: number) => i === index ? { ...qq, question_text: e.target.value } : qq))}
                      multiline
                      rows={2}
                    />
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 15%' } }}>
                    <TextField
                      fullWidth
                      label="Points"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={q.points}
                      onChange={e => setQuestions(prev => prev.map((qq: any, i: number) => i === index ? { ...qq, points: Number(e.target.value) } : qq))}
                      error={q.points < 1}
                      helperText={q.points < 1 ? 'Points must be at least 1' : ''}
                    />
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 20%' } }}>
                    <TextField
                      fullWidth
                      label="Type"
                      select
                      value={q.question_type}
                      onChange={e => setQuestions(prev => prev.map((qq: any, i: number) => i === index ? { ...qq, question_type: e.target.value } : qq))}
                    >
                      <MenuItem value="mcq">Multiple Choice</MenuItem>
                      <MenuItem value="true_false">True/False</MenuItem>
                      <MenuItem value="short_answer">Short Answer</MenuItem>
                    </TextField>
                  </Box>
                </Box>
                {q.question_type === 'mcq' || q.question_type === 'true_false' ? (
                  <Box sx={{ fontWeight: 'bold', marginTop: 2, marginBottom: 1 }}>Options</Box>
                ) : null}
                {q.question_type === 'mcq' || q.question_type === 'true_false' ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {q.options.map((opt: any, optIndex: number) => (
                      <Box key={opt.id || optIndex} sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          fullWidth
                          label={`Option ${optIndex + 1}`}
                          value={opt.option_text}
                          onChange={e => setQuestions(prev => prev.map((qq: any, i: number) => i === index ? {
                            ...qq,
                            options: qq.options.map((ooo: any, oooIndex: number) => oooIndex === optIndex ? { ...ooo, option_text: e.target.value } : ooo)
                          } : qq))}
                        />
                        <input
                          type="checkbox"
                          checked={opt.is_correct}
                          onChange={() => setQuestions(prev => prev.map((qq: any, i: number) => i === index ? {
                            ...qq,
                            options: qq.options.map((ooo: any, oooIndex: number) => oooIndex === optIndex ? { ...ooo, is_correct: !ooo.is_correct } : ooo)
                          } : qq))}
                          style={{ marginLeft: 1 }}
                        />{' '}
                        Correct
                      </Box>
                    ))}
                    <Box sx={{ flex: '1 1 100%' }}>
                      <Button variant="outlined" onClick={() => setQuestions(prev => prev.map((qq: any, i: number) => i === index ? {
                        ...qq,
                        options: [...qq.options, { option_text: '', is_correct: false }]
                      } : qq))} sx={{ marginTop: 1 }}>Add Option</Button>
                    </Box>
                  </Box>
                ) : q.question_type === 'short_answer' ? (
                  <Box sx={{ fontWeight: 'bold', marginTop: 2, marginBottom: 1 }}>Correct Answer (for grading)</Box>
                ) : null}
                {q.question_type === 'short_answer' ? (
                  <TextField
                    fullWidth
                    label="Correct Answer (for grading)"
                    value={q.correct_answer_text || ''}
                    onChange={e => setQuestions(prev => prev.map((qq: any, i: number) => i === index ? { ...qq, correct_answer_text: e.target.value } : qq))}
                    multiline
                    rows={2}
                  />
                ) : null}
              </Box>
            ))}
            <Button variant="outlined" onClick={() => setQuestions(prev => [...prev, emptyQuestion()])} sx={{ marginTop: 1 }}>Add Question</Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ marginTop: 2, marginBottom: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={submitting || !title || !courseId || !classLevel || questions.some((q: any) => !q.question_text)}>
          {submitting ? <CircularProgress size={24} /> : (initialQuiz ? 'Update Quiz' : 'Create Quiz')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ResultsDialog for teachers
const ResultsDialog: React.FC<{ quiz: Quiz; onClose: () => void }> = ({ quiz, onClose }) => {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAttempt, setSelectedAttempt] = useState<any | null>(null);
  const [attemptDetails, setAttemptDetails] = useState<any | null>(null);
  const [grading, setGrading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await getQuizResults(quiz.id);
        setResults(res as any[]);
      } finally {
        setLoading(false);
      }
    })();
  }, [quiz.id]);

  const handleViewAttempt = async (attemptId: number) => {
    setSelectedAttempt(attemptId);
    setLoading(true);
    try {
      const details = await getAttemptResults(attemptId);
      setAttemptDetails(details);
    } finally {
      setLoading(false);
    }
  };

  // Manual grading for short answers (mock, to be replaced with real API)
  const handleGrade = async (responseId: number, isCorrect: boolean) => {
    setGrading(true);
    setError(null);
    try {
      const updated = await gradeQuizResponse(responseId, isCorrect);
      setAttemptDetails((prev: any) => ({
        ...prev,
        responses: prev.responses.map((r: any) => r.id === responseId ? { ...r, is_correct: (updated as any).is_correct } : r),
        score: (updated as any).attempt_score !== undefined ? (updated as any).attempt_score : prev.score,
      }));
    } catch (e: any) {
      setError('Failed to update grade');
    } finally {
      setGrading(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>Quiz Results: {quiz.title} {quiz.class_level && (
        <span style={{ marginLeft: 8, color: '#888', fontSize: '0.95em' }}>
          [Class: {quiz.class_level}]
        </span>
      )}</DialogTitle>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      {loading && (
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress />
            <Box sx={{ marginTop: 1 }}>Loading results...</Box>
          </Box>
        </DialogContent>
      )}
      {!loading && !selectedAttempt && (
        <DialogContent>
          <Box sx={{ fontWeight: 'bold', marginBottom: 1 }}>Student Attempts</Box>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Score</th>
                  <th>Completed At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map((a: any) => (
                  <tr key={a.attempt_id}>
                    <td>{a.student_id}</td>
                    <td>{a.score}</td>
                    <td>{a.completed_at ? new Date(a.completed_at).toLocaleString() : '-'}</td>
                    <td><Button onClick={() => handleViewAttempt(a.attempt_id)}>View</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </DialogContent>
      )}
      {!loading && selectedAttempt && attemptDetails && (
        <DialogContent>
          <DialogActions>
            <Button onClick={() => { setSelectedAttempt(null); setAttemptDetails(null); }}>Back to List</Button>
          </DialogActions>
          <Box sx={{ fontWeight: 'bold' }}>Attempt by Student {attemptDetails.student_id}</Box>
          <Box sx={{ marginBottom: 1 }}>Score: {attemptDetails.score}</Box>
          <Box sx={{ fontWeight: 'bold', marginBottom: 1 }}>Responses</Box>
          <Box sx={{ overflowX: 'auto' }}>
            <ul style={{ marginTop: 8 }}>
              {attemptDetails.responses.map((resp: any, idx: number) => {
                const q = quiz.questions && Array.isArray(quiz.questions) ? quiz.questions.find((qq: any) => qq.id === resp.question_id) : undefined;
                return (
                  <li key={resp.id} style={{ marginBottom: 8 }}>
                    <Box sx={{ fontWeight: 'bold' }}>Q{idx + 1}:</Box> {q?.question_text || ''} <span style={{ color: resp.is_correct === true ? 'green' : resp.is_correct === false ? 'red' : '#888' }}>({resp.is_correct === true ? 'Correct' : resp.is_correct === false ? 'Incorrect' : 'Pending'})</span>
                    <Box sx={{ marginLeft: 2 }}>
                      {q?.question_type === 'short_answer' ? (
                        <>
                          <Box sx={{ marginBottom: 0.5 }}>Answer: <i>{resp.answer_text}</i></Box>
                          <Button disabled={grading} onClick={() => handleGrade(resp.id, true)} sx={{ marginRight: 1 }}>Mark Correct</Button>
                          <Button disabled={grading} onClick={() => handleGrade(resp.id, false)}>Mark Incorrect</Button>
                        </>
                      ) : q?.question_type === 'mcq' || q?.question_type === 'true_false' ? (
                        <Box sx={{ marginBottom: 0.5 }}>Selected Option: {resp.selected_option_id}</Box>
                      ) : null}
                    </Box>
                  </li>
                );
              })}
            </ul>
          </Box>
          {error && (
            <Alert severity="error" sx={{ marginTop: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

const Quizzes: React.FC = () => {
  const { user } = useAuth();
  const { showError, showInfo, showSuccess } = useFeedback();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [takingQuizId, setTakingQuizId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState<Quiz | null>(null);
  const [showResultsQuiz, setShowResultsQuiz] = useState<Quiz | null>(null);
  const [classLevelFilter, setClassLevelFilter] = useState<string>('');
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const fetchQuizzes = async () => {
      setLoading(true);
      try {
        setQuizzes(await listQuizzes());
      } catch (err: any) {
        if (err?.response?.data?.detail === 'Session expired' || err?.message === 'Session expired') {
          showError('Your session has expired. Please log in again.');
        } else {
          showError(err?.response?.data?.detail || err?.message || 'Failed to load quizzes');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchQuizzes();
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [showError]);

  // Split quizzes into live and regular
  const liveQuizzes = quizzes.filter(q => q.mode === 'live' && q.start_time && q.end_time);
  const regularQuizzes = quizzes.filter(q => !(q.mode === 'live' && q.start_time && q.end_time));

  // Helper for countdown
  const getCountdown = (start: string, end: string) => {
    const startMs = dayjs(start).valueOf();
    const endMs = dayjs(end).valueOf();
    if (now < startMs) {
      const diff = startMs - now;
      return { status: 'upcoming', diff };
    } else if (now >= startMs && now <= endMs) {
      const diff = endMs - now;
      return { status: 'open', diff };
    } else {
      return { status: 'ended', diff: 0 };
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <h1>Quizzes</h1>
      {/* Class Level Filter Dropdown */}
      <Box sx={{ mb: 2, maxWidth: 300 }}>
        <TextField
          select
          fullWidth
          label="Filter by Class Level"
          value={classLevelFilter}
          onChange={e => setClassLevelFilter(e.target.value)}
        >
          <MenuItem value=''>All Class Levels</MenuItem>
          {classLevels.map(level => <MenuItem key={level} value={level}>{level}</MenuItem>)}
        </TextField>
      </Box>
      {/* TODO: Show create quiz button for teachers only */}
      {user?.role === 'teacher' || user?.role === 'admin' && (
        <Button variant="contained" onClick={() => setShowCreate(true)}>Create Quiz (Teacher)</Button>
      )}
      <h2>Live Quizzes</h2>
      {liveQuizzes.length === 0 ? (
        <Box sx={{ mb: 2, color: '#888' }}>No live quizzes scheduled.</Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          {liveQuizzes.map(quiz => {
            const countdown = getCountdown(quiz.start_time!, quiz.end_time!);
            let countdownText = '';
            if (countdown.status === 'upcoming') {
              const mins = Math.floor(countdown.diff / 60000);
              const secs = Math.floor((countdown.diff % 60000) / 1000);
              countdownText = `Opens in ${mins}m ${secs}s`;
            } else if (countdown.status === 'open') {
              const mins = Math.floor(countdown.diff / 60000);
              const secs = Math.floor((countdown.diff % 60000) / 1000);
              countdownText = `Closes in ${mins}m ${secs}s`;
            } else {
              countdownText = 'Closed';
            }
            return (
              <Box key={quiz.id} sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 2, border: '1px solid #eee', borderRadius: 2, background: '#f9f9fc' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <b>{quiz.title}</b>
                  {quiz.class_level && (
                    <span style={{ marginLeft: 8, color: '#888', fontSize: '0.95em' }}>
                      [Class: {quiz.class_level}]
                    </span>
                  )}
                  <Box sx={{ color: '#888', fontSize: '0.95em' }}>
                    {dayjs(quiz.start_time).format('YYYY-MM-DD HH:mm')} - {dayjs(quiz.end_time).format('HH:mm')}
                  </Box>
                </Box>
                <Box sx={{ mr: 2, fontWeight: 'bold', color: countdown.status === 'open' ? 'green' : countdown.status === 'upcoming' ? '#888' : 'red' }}>{countdownText}</Box>
                {countdown.status === 'open' ? (
                  <Button variant="contained" color="primary" onClick={() => setTakingQuizId(quiz.id)}>Join Now</Button>
                ) : countdown.status === 'upcoming' ? (
                  <Button variant="outlined" disabled>Not Open</Button>
                ) : (
                  <Button variant="outlined" disabled>Closed</Button>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      <Box sx={{ marginTop: 2 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center' }}>Loading quizzes...</Box>
        ) : (classLevelFilter ? quizzes.filter(q => q.class_level === classLevelFilter) : quizzes).length === 0 ? (
          <Box sx={{ textAlign: 'center' }}>No quizzes found.</Box>
        ) : (
          <Box sx={{ listStyle: 'none', padding: 0 }}>
            {(classLevelFilter ? quizzes.filter(q => q.class_level === classLevelFilter) : quizzes).map((quiz: Quiz) => (
              <Box key={quiz.id} sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  {quiz.title || 'Untitled Quiz'}
                  {quiz.class_level && (
                    <span style={{ marginLeft: 8, color: '#888', fontSize: '0.95em' }}>
                      [Class: {quiz.class_level}]
                    </span>
                  )}
                </Box>
                {quiz.duration_minutes && (
                  <Box sx={{ marginLeft: 1, color: '#888' }}>
                    (Timed: {quiz.duration_minutes} min)
                  </Box>
                )}
                {/* TODO: Show take quiz button for students only */}
                <Button variant="outlined" onClick={() => setTakingQuizId(quiz.id)}>Take Quiz (Student)</Button>
                {/* Edit button for teachers */}
                {user?.role === 'teacher' || user?.role === 'admin' && (
                  <Button variant="outlined" onClick={() => setEditingQuiz(quiz)} sx={{ marginLeft: 1 }}>Edit</Button>
                )}
                {/* TODO: Show view results button for teachers */}
                {user?.role === 'teacher' || user?.role === 'admin' && (
                  <Button variant="outlined" onClick={() => setShowResultsQuiz(quiz)} sx={{ marginLeft: 1 }}>View Results</Button>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {/* Quiz Taking Dialog */}
      {takingQuizId && (
        <QuizTakingDialog quizId={takingQuizId} onClose={() => setTakingQuizId(null)} />
      )}
      {/* Quiz Create/Edit Dialog */}
      {(showCreate || editingQuiz) && (
        <QuizCreateDialog
          onClose={() => { setShowCreate(false); setEditingQuiz(null); }}
          onCreated={async () => {
            setShowCreate(false);
            setEditingQuiz(null);
            setLoading(true);
            setQuizzes(await listQuizzes());
            setLoading(false);
          }}
          initialQuiz={editingQuiz}
        />
      )}
      {/* Results Dialog */}
      {showResultsQuiz && (
        <ResultsDialog quiz={showResultsQuiz} onClose={() => setShowResultsQuiz(null)} />
      )}
    </Box>
  );
};

export { QuizTakingDialog };

export default Quizzes; 